import { useCallback, useEffect, useState } from 'react';
import type { ApiReport } from '@/lib/types';
import { getAdminReports, resolveReport, dismissReport, removeReportedContent } from '@/services/adminService';
import { reportStatusMeta, entityTypeMeta } from '../adminMeta';
import ConfirmModal from './ConfirmModal';

type StatusFilter = 'all' | 'open' | 'resolved' | 'dismissed';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Tous' },
  { value: 'open', label: 'Ouverts' },
  { value: 'resolved', label: 'Résolus' },
  { value: 'dismissed', label: 'Ignorés' },
];

interface ActionTarget {
  id: string;
  type: 'resolve' | 'dismiss' | 'remove';
}

export default function ReportsTab() {
  const [reports, setReports] = useState<ApiReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [pending, setPending] = useState<string | null>(null);
  const [target, setTarget] = useState<ActionTarget | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminReports();
      setReports(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (t: ActionTarget) => {
    setPending(t.id);

    try {
      if (t.type === 'resolve') {
        await resolveReport(t.id);
      } else if (t.type === 'dismiss') {
        await dismissReport(t.id);
      } else {
        await removeReportedContent(t.id);
      }

      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Action impossible'
      );
    } finally {
      setPending(null);
      setTarget(null);
    }
  }

  const handleAction = (t: ActionTarget) => {
    if (t.type === 'remove') {
      setTarget(t);
      return;
    }

    void runAction(t);
  };

  const filtered = reports.filter((r) => {
    const s = (r.status ?? '').toLowerCase();
    if (filter === 'open') return s !== 'resolved' && s !== 'dismissed';
    if (filter === 'resolved') return s === 'resolved';
    if (filter === 'dismissed') return s === 'dismissed';
    return true;
  });

  const openCount = reports.filter((r) => {
    const s = (r.status ?? '').toLowerCase();
    return s !== 'resolved' && s !== 'dismissed';
  }).length;

  return (
    <div>
      {/* Header + filter */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          {openCount} signalement{openCount > 1 ? 's' : ''} à traiter
        </p>
        <div className="inline-flex rounded-full bg-background-100 p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${filter === f.value
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-700'
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mt-4 flex flex-col gap-3">
        {loading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-background-200 rounded-lg p-5 animate-pulse">
              <div className="h-3 bg-background-100 rounded w-24 mb-3"></div>
              <div className="h-4 bg-background-100 rounded w-3/4"></div>
            </div>
          ))
        ) : error ? (
          <div className="bg-white border border-background-200 rounded-lg p-8 text-center">
            <p className="text-sm text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-shield-check-line text-2xl"></i>
            </div>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              Aucun signalement
            </h3>
            <p className="mt-1 text-sm text-foreground-500 max-w-xs mx-auto">
              Tout est calme par ici. Les signalements apparaîtront dans cet onglet.
            </p>
          </div>
        ) : (
          filtered.map((r) => (<ReportCard key={r.id} report={r} pending={pending === r.id} onAction={handleAction} />))
        )}
      </div>

      <ConfirmModal
        open={target?.type === 'remove'}
        title="Retirer ce contenu ?"
        message="Le contenu signalé sera retiré définitivement. Cette action est irréversible."
        confirmLabel="Retirer le contenu"
        tone="danger"
        loading={!!target && pending === target.id}
        onConfirm={() => target && runAction(target)}
        onCancel={() => setTarget(null)}
      />
    </div>
  );
}

function ReportCard({ report, pending, onAction }: { report: ApiReport; pending: boolean; onAction: (t: ActionTarget) => void }) {
  const status = reportStatusMeta(report.status);
  const entity = entityTypeMeta(report.entityType);
  const reporter = report.reporter?.displayName ?? report.reporter?.name ?? report.reporterName ?? report.reporterAlias ?? 'Anonyme';
  const content = report.content ?? report.reportedContent ?? null;
  const isOpen = (report.status ?? '').toLowerCase() !== 'resolved' && (report.status ?? '').toLowerCase() !== 'dismissed';

  return (
    <div className="bg-white border border-background-200 rounded-lg p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${entity.className}`}>
            <i className={`${entity.icon} text-sm`}></i>
            {entity.label}
          </span>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.className}`}>
            <i className={`${status.icon} text-sm`}></i>
            {status.label}
          </span>
        </div>
        <span className="text-xs text-foreground-400 whitespace-nowrap">
          {new Date(report.createdAt).toLocaleString('fr-FR', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>

      <p className="mt-3 text-sm text-foreground-700">
        <span className="font-semibold text-foreground-950">Motif :</span> {report.reason}
      </p>

      {content && (
        <div className="mt-2 px-3 py-2 bg-background-50 rounded-md text-sm text-foreground-600 italic border-l-2 border-background-200">
          « {content} »
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-foreground-400">
          Signalé par <span className="font-medium text-foreground-600">{reporter}</span>
        </p>

        {isOpen ? (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              disabled={pending}
              onClick={() => onAction({ id: report.id, type: 'dismiss' })}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Ignorer
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => onAction({ id: report.id, type: 'remove' })}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-accent-900 bg-accent-100 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Retirer le contenu
            </button>
            <button
              type="button"
              disabled={pending}
              onClick={() => onAction({ id: report.id, type: 'resolve' })}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pending ? 'Traitement…' : 'Résoudre'}
            </button>
          </div>
        ) : (
          <span className="text-xs text-foreground-400">Traité</span>
        )}
      </div>
    </div>
  );
}